import { Fragment, type ReactElement } from 'react';
import { Label, ReferenceLine, XAxis } from 'recharts';

import type { Segmentation } from '@core';
import { constants } from '@core';

interface ValidPosition {
    label: string;
    value: number;
}

const style = {
    color: '#94A3B8',
    WebkitFontSmoothing: 'antialiased',
    fontSize: 10,
    fontWeight: 200,
    letterSpacing: '0.05em',
    fontFamily: 'Inter',
    backgroundColor: 'white',
};

const floorStyle = {
    fill: 'white',
    WebkitFontSmoothing: 'antialiased',
    fontSize: 22,
    fontWeight: 700,
    letterSpacing: '0.05em',
    fontFamily: 'Inter',
    backgroundColor: 'white',
};

const getValidPositions = (segmentation: Segmentation): ValidPosition[] => {
    return Object.entries(segmentation)
        .filter(([, value]) => value !== null)
        .map(([key, value]) => ({
            label: key,
            value,
        }));
};

export const renderXAxis = ({
    isQuickAnalysis,
    segmentation,
    axisKey,
}: {
    isQuickAnalysis: boolean;
    segmentation: {
        current: Segmentation;
        comparison: Segmentation | null;
    };
    axisKey: string;
}): ReactElement => {
    const frameNumbers = Object.values(segmentation.current);
    const domain: [number, number] = [frameNumbers.at(0) ?? 0, frameNumbers.at(-1) ?? 0] as [number, number];
    const allowedBottomLabels: string[] = ['P1', 'P2', 'P3', 'P4', 'P7', 'P9', 'P10'];

    const validPositions = getValidPositions(segmentation.current);
    if (!segmentation.current['P7']) {
        throw new Error('Reference frame is required for current reference lines');
    }
    const referenceLines = validPositions.map(({ label, value }) => {
        const showMilliseconds = allowedBottomLabels.includes(label);

        const milliseconds = showMilliseconds
            ? Math.round(((value - segmentation.current['P7']) / constants.CAMERA_FPS) * 1000)
            : 0;
        const sign = milliseconds >= 0 ? '+' : '';

        // Comparison graph data is synced to P7, meaning that other positions are most definitely not synced.
        // We'll fade out the other position labels to indicate that they shouldn't be considered "synced".
        const unsyncedPositionLabel = segmentation.comparison !== null && label !== 'P7';

        return (
            <ReferenceLine
                key={label}
                x={value}
                strokeDasharray="3 3"
                yAxisId={axisKey}
                stroke={style.color}
                opacity={0.4}
            >
                <Label
                    value={label}
                    offset={16}
                    position="top"
                    style={style}
                    opacity={unsyncedPositionLabel ? 1 / 3 : 1}
                />
                {showMilliseconds && (
                    <Label value={`${sign}${milliseconds}ms`} offset={16} position="bottom" style={style} />
                )}
            </ReferenceLine>
        );
    });

    return (
        <Fragment>
            {referenceLines}
            {isQuickAnalysis && (
                <XAxis
                    visibility="hidden"
                    dataKey="frame"
                    height={0}
                    scale="time"
                    type="number"
                    domain={domain}
                    ticks={frameNumbers}
                    tick={false}
                />
            )}
        </Fragment>
    );
};

export const renderXAxisFloor = ({
    isQuickAnalysis,
    segmentation,
    axisKey,
}: {
    isQuickAnalysis: boolean;
    segmentation: {
        current: Segmentation;
        comparison: Segmentation | null;
    };
    axisKey: string;
}): ReactElement => {
    const frameNumbers = Object.values(segmentation.current);
    const domain: [number, number] = [frameNumbers.at(0) ?? 0, frameNumbers.at(-1) ?? 0] as [number, number];

    const validPositions = getValidPositions(segmentation.current);

    if (!segmentation.current['P7']) {
        throw new Error('Reference frame is required for current reference lines');
    }
    const labels = ['P1', 'P2', 'P4', 'P7'];
    const referenceLines = validPositions.map(({ label, value }) => {
        // Comparison graph data is synced to P7, meaning that other positions are most definitely not synced.
        // We'll fade out the other position labels to indicate that they shouldn't be considered "synced".
        // const unsyncedPositionLabel = segmentation.comparison !== null && label !== 'P7';

        return (
            <ReferenceLine
                key={label}
                x={value}
                strokeDasharray="3 3"
                yAxisId={axisKey}
                stroke={style.color}
                opacity={0.4}
            >
                {labels.includes(label) && (
                    <Label fontSize={125} value={label} offset={16} position="top" style={floorStyle} />
                )}
            </ReferenceLine>
        );
    });

    return (
        <Fragment>
            {referenceLines}
            {isQuickAnalysis && (
                <XAxis
                    visibility="hidden"
                    dataKey="frame"
                    height={0}
                    scale="time"
                    type="number"
                    domain={domain}
                    ticks={frameNumbers}
                    tick={false}
                />
            )}
        </Fragment>
    );
};
